/**
 * CrmMentionsInput
 *
 * Uses react-mentions for proper mention chips: real DOM elements, cursor never
 * inside a chip, one backspace deletes whole mention. Replaces the textarea+overlay
 * hack when in Sidekick/CRM mention mode.
 */

"use client"

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { MentionsInput as MentionsInputLib, Mention as MentionLib } from "react-mentions"
import {
  IconAt,
  IconBuildingStore,
  IconRobotFace,
  IconSparkle2,
  IconSparkleHighlight,
  IconUserCircle
} from "@tabler/icons-react"
import { cn } from "@/lib/utils"
import type {
  StructuredMentionConfig,
  StructuredMentionSuggestion
} from "@/context/chat-handler"

/** Type assertion: react-mentions class components don't match React 18 JSX types (refs) */
const MentionsInput = MentionsInputLib as React.ComponentType<any>
const Mention = MentionLib as React.ComponentType<any>
const CRM_MENTION_SUGGESTIONS_Z_INDEX = 2147483100

export type CrmMentionItem = { type: string; id: string; label: string }

type MentionSuggestionItem = {
  avatarUrl?: string | null
  category?: string
  description?: string | null
  display: string
  id: string
  kind?: StructuredMentionSuggestion['kind']
}

const defaultStyle = {
  control: {
    fontSize: 16,
    lineHeight: 1.5
  },
  input: {
    margin: 0,
    padding: "14px 8px",
    border: "none",
    outline: "none",
    background: "transparent",
    resize: "none" as const,
    overflow: "hidden",
    fontSize: 16,
    lineHeight: 1.5,
    color: "hsl(var(--foreground))"
  },
  highlighter: {
    padding: "14px 8px",
    border: "none",
    fontSize: 16,
    lineHeight: 1.5,
    color: "hsl(var(--foreground))"
  },
  suggestions: {
    maxWidth: "calc(100vw - 24px)",
    minWidth: "min(17.5rem, calc(100vw - 24px))",
    overflow: "hidden",
    borderRadius: 12,
    border: "1px solid hsl(var(--border) / 0.5)",
    background: "hsl(var(--popover))",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    color: "hsl(var(--popover-foreground))",
    width: "min(34rem, calc(100vw - 24px))",
    zIndex: CRM_MENTION_SUGGESTIONS_Z_INDEX,
    list: {
      maxHeight: 360,
      maxWidth: "100%",
      minWidth: 0,
      overflowX: "hidden",
      overflowY: "auto",
      borderRadius: "inherit",
      background: "transparent",
      width: "100%"
    },
    item: {
      padding: 0,
      fontSize: 13,
      cursor: "pointer"
    }
  }
}

/* Keep mention metrics identical to textarea text so mobile Safari does not double-render it offset. */
const mentionStyle = {
  backgroundColor: "hsl(var(--muted))",
  color: "hsl(var(--foreground))",
  borderRadius: 6,
  boxShadow: "inset 0 0 0 1px hsl(var(--border))",
  display: "inline",
  fontFamily: "inherit"
}

export interface CrmMentionsInputProps {
  value: string
  onChange: (value: string) => void
  onAdd?: (id: string, display: string) => void
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  workspaceId: string | null
  mentionConfig?: StructuredMentionConfig | null
  inputRef?: React.RefObject<HTMLTextAreaElement | null>
  className?: string
  disabled?: boolean
  "aria-label"?: string
}

/** Strip "lead: ", "contact: ", "company: " prefix when rendering mention chip (displayTransform is built-in) */
function displayTransform(_id: string, display: string): string {
  return display.replace(/^(lead|contact|company):\s*/i, "").trim() || display
}

/**
 * Fetch CRM mention suggestions. Returns items in react-mentions shape:
 * { id: "lead:uuid", display: "Label" } so dropdown shows name only; markup is @[Label](lead:uuid).
 */
async function fetchSuggestions(
  workspaceId: string,
  query: string
): Promise<MentionSuggestionItem[]> {
  const res = await fetch(
    `/api/crm/mentions?q=${encodeURIComponent(query)}&workspace_id=${encodeURIComponent(workspaceId)}&limit=50`
  )
  if (!res.ok) return []
  const data = await res.json()
  const list = Array.isArray(data) ? data : []
  return list.map((item: CrmMentionItem) => ({
    category: getSuggestionCategory(item.type),
    id: `${item.type}:${item.id}`,
    display: item.label,
    kind: getSuggestionKind(item.type)
  }))
}

function toMentionItems(
  items: StructuredMentionSuggestion[]
): MentionSuggestionItem[] {
  return items.map(item => ({
    avatarUrl: item.avatarUrl,
    category: item.category ?? getSuggestionCategory(item.id),
    description: item.description,
    id: item.id,
    display: item.display,
    kind: item.kind ?? getSuggestionKind(item.id)
  }))
}

function getSuggestionKind(value: string): StructuredMentionSuggestion['kind'] {
  const prefix = value.split(':')[0]?.toLowerCase()

  if (prefix === 'assistant') return 'assistant'
  if (prefix === 'company') return 'company'
  if (prefix === 'contact') return 'contact'
  if (prefix === 'lead') return 'lead'
  if (prefix === 'member') return 'member'
  if (prefix === 'losi') return 'platform'
  if (prefix === 'super-agent') return 'super_agent'

  return undefined
}

function getSuggestionCategory(value: string) {
  const kind = getSuggestionKind(value)

  if (kind === 'assistant') return 'Assistants'
  if (kind === 'company') return 'Companies'
  if (kind === 'contact' || kind === 'lead' || kind === 'member') return 'Contacts'
  if (kind === 'platform') return 'Platform'
  if (kind === 'super_agent') return 'Losi Super Agents'

  return 'Mentions'
}

function getSuggestionIcon(suggestion: MentionSuggestionItem): ReactNode {
  if (suggestion.avatarUrl) {
    return (
      <span
        aria-hidden="true"
        className="size-7 shrink-0 rounded-full bg-cover bg-center"
        style={{ backgroundImage: `url(${suggestion.avatarUrl})` }}
      />
    )
  }

  if (suggestion.kind === 'super_agent') {
    return <IconSparkleHighlight className="size-4" stroke={1.8} />
  }

  if (suggestion.kind === 'assistant') {
    return <IconRobotFace className="size-4" stroke={1.8} />
  }

  if (suggestion.kind === 'company') {
    return <IconBuildingStore className="size-4" stroke={1.8} />
  }

  if (suggestion.kind === 'platform') {
    return <IconSparkle2 className="size-4" stroke={1.8} />
  }

  if (suggestion.kind === 'everyone') {
    return <IconAt className="size-4" stroke={1.8} />
  }

  return <IconUserCircle className="size-4" stroke={1.8} />
}

function getSuggestionIconClassName(kind: StructuredMentionSuggestion['kind']) {
  if (kind === 'super_agent') {
    return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
  }

  if (kind === 'assistant') {
    return 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300'
  }

  if (kind === 'platform') {
    return 'border-primary/20 bg-primary/10 text-primary'
  }

  if (kind === 'everyone') {
    return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
  }

  return 'border-border bg-muted text-muted-foreground'
}

function renderSuggestion(
  suggestion: MentionSuggestionItem,
  _search: string,
  highlightedDisplay: ReactNode,
  _index: number,
  focused: boolean
) {
  return (
    <div
      className={cn(
        'flex w-full min-w-0 max-w-full items-center gap-3 overflow-hidden px-3 py-2',
        focused && 'bg-accent'
      )}
    >
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-lg border',
          getSuggestionIconClassName(suggestion.kind)
        )}
      >
        {getSuggestionIcon(suggestion)}
      </span>
      <div className="min-w-0 max-w-full flex-1 overflow-hidden">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate font-medium">
            {highlightedDisplay}
          </span>
          <span className="border-border text-muted-foreground max-w-36 shrink-0 truncate rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-normal">
            {suggestion.category ?? getSuggestionCategory(suggestion.id)}
          </span>
        </div>
        {suggestion.description ? (
          <div
            className="text-muted-foreground w-full min-w-0 max-w-full truncate text-xs"
            title={suggestion.description}
          >
            {suggestion.description}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function CrmMentionsInput({
  value,
  onChange,
  onAdd,
  onBlur,
  onKeyDown,
  onPaste,
  placeholder = "Ask about this page…",
  workspaceId,
  mentionConfig,
  inputRef,
  className,
  disabled,
  "aria-label": ariaLabel
}: CrmMentionsInputProps) {
  const fetchRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [suggestionsPortalHost, setSuggestionsPortalHost] = useState<Element | null>(null)

  useEffect(() => {
    setSuggestionsPortalHost(document.body)
  }, [])

  const data = useCallback(
    (search: string, callback: (items: MentionSuggestionItem[]) => void) => {
      if (fetchRef.current) clearTimeout(fetchRef.current)
      if (search.length < 1 && !mentionConfig?.allowEmptyQuery) {
        callback([])
        return
      }
      fetchRef.current = setTimeout(async () => {
        if (mentionConfig) {
          const items = await mentionConfig.getSuggestions(search)
          callback(toMentionItems(items))
        } else if (workspaceId) {
          const items = await fetchSuggestions(workspaceId, search)
          callback(items)
        } else {
          callback([])
        }
        fetchRef.current = null
      }, 200)
    },
    [mentionConfig, workspaceId]
  )

  const markup = mentionConfig?.markup || "@[__display__](__id__)"
  const trigger = mentionConfig?.trigger || "@"
  const transformDisplay =
    mentionConfig?.mode === "crm"
      ? displayTransform
      : (_id: string, display: string) => display

  return (
    <div className={cn("crm-mentions-wrapper bg-transparent", className)}>
      <MentionsInput
        value={value}
        onChange={(_e: React.ChangeEvent<HTMLTextAreaElement>, newValue: string) => onChange(newValue)}
        onBlur={onBlur as any}
        onKeyDown={onKeyDown as any}
        onPaste={onPaste}
        placeholder={placeholder}
        inputRef={inputRef}
        disabled={disabled}
        aria-label={ariaLabel}
        allowSuggestionsAboveCursor
        forceSuggestionsAboveCursor
        suggestionsPortalHost={suggestionsPortalHost ?? undefined}
        a11ySuggestionsListLabel="Mention suggestions"
        style={defaultStyle}
        className="crm-mentions-input"
        classNames={{
          control: "crm-mentions-control",
          input: "crm-mentions-input-el",
          highlighter: "crm-mentions-highlighter"
        }}
        singleLine={false}
      >
        {/* Canonical markup @[__display__](__id__): sync with message.tsx + message-markdown link→chip */}
        <Mention
          trigger={trigger}
          data={data}
          markup={markup}
          displayTransform={transformDisplay}
          renderSuggestion={renderSuggestion}
          style={mentionStyle}
          className="crm-mentions-pill"
          onAdd={onAdd}
          appendSpaceOnAdd
        />
      </MentionsInput>
    </div>
  )
}
